import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Copy, DollarSign, Link2, ShieldCheck, TrendingUp, Users } from 'lucide-react';
import { API, toast } from '@/App';
import SearchBar from '@/components/shared/SearchBar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  formatAffiliateCurrency,
  formatAffiliateDateTime,
  getAffiliateStatusLabel,
  getAffiliateStatusToneClasses,
} from '@/utils/affiliateWorkspace';

const ActiveAmbassadorsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [summary, setSummary] = useState({});
  const [programSettings, setProgramSettings] = useState({});
  const [ambassadors, setAmbassadors] = useState([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/ambassadors`);
      const payload = response.data || {};
      setSummary(payload.summary || {});
      setProgramSettings(payload.program_settings || {});
      setAmbassadors(Array.isArray(payload.items) ? payload.items : []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Impossible de charger les ambassadeurs actifs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return ambassadors;
    }
    return ambassadors.filter((item) => {
      const haystack = [item.user_name, item.user_email, item.affiliate_code]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');
      return haystack.includes(query);
    });
  }, [ambassadors, searchQuery]);

  const handleToggleStatus = async (affiliateId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/affiliates/${affiliateId}`, { is_active: !currentStatus });
      toast.success(currentStatus ? 'Ambassadeur suspendu.' : 'Ambassadeur reactive.');
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de la mise a jour.');
    }
  };

  if (loading) {
    return <div className="py-8 text-sm text-slate-500">Chargement des ambassadeurs actifs...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <Card><CardContent className="pt-6 text-center"><Users className="mx-auto mb-2 h-8 w-8 text-sky-600" /><p className="text-2xl font-bold">{Number(summary.total || 0)}</p><p className="text-sm text-slate-500">Ambassadeurs</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><ShieldCheck className="mx-auto mb-2 h-8 w-8 text-emerald-600" /><p className="text-2xl font-bold">{Number(summary.active || 0)}</p><p className="text-sm text-slate-500">Actifs</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><TrendingUp className="mx-auto mb-2 h-8 w-8 text-cyan-600" /><p className="text-2xl font-bold">{Number(summary.subscriptions_generated || 0)}</p><p className="text-sm text-slate-500">Abonnements generes</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><Link2 className="mx-auto mb-2 h-8 w-8 text-violet-600" /><p className="text-2xl font-bold">{Number(summary.total_registrations || 0)}</p><p className="text-sm text-slate-500">Inscriptions</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><DollarSign className="mx-auto mb-2 h-8 w-8 text-amber-600" /><p className="text-2xl font-bold">{formatAffiliateCurrency(summary.total_commissions || 0)}</p><p className="text-sm text-slate-500">Commissions</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Ambassadeurs actifs</CardTitle>
              <CardDescription>
                Pilotage separe des performances ambassadeur. Candidatures en attente: {Number(summary.pending_applications || 0)}.
              </CardDescription>
            </div>
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Rechercher un ambassadeur..."
              className="max-w-xs"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <Badge variant="outline">Ambassadeur: {Number(programSettings.ambassador_percent || 0)}%</Badge>
            <Badge variant="outline">Remise vendeur parraine: {Number(programSettings.referral_discount_percent || 0)}%</Badge>
            <Badge variant="outline">Minimum retrait: {formatAffiliateCurrency(programSettings.min_payout_amount || 0)}</Badge>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ambassadeur</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Inscriptions</TableHead>
                <TableHead>Vendeurs actifs</TableHead>
                <TableHead>Abonnements</TableHead>
                <TableHead>Commissions</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="font-medium">{item.user_name || '-'}</div>
                    <div className="text-xs text-slate-500">{item.user_email || '-'}</div>
                    <div className="text-xs text-slate-400">{formatAffiliateDateTime(item.last_registration_at)}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="rounded bg-slate-100 px-2 py-1 text-xs">{item.affiliate_code}</code>
                      {item.referral_link ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(item.referral_link);
                              toast.success('Lien copie.');
                            } catch (error) {
                              toast.error('Impossible de copier le lien.');
                            }
                          }}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>{Number(item.total_referrals || 0).toLocaleString('fr-FR')}</TableCell>
                  <TableCell>{Number(item.active_referred_vendors || 0).toLocaleString('fr-FR')}</TableCell>
                  <TableCell>{Number(item.subscriptions_generated || 0).toLocaleString('fr-FR')}</TableCell>
                  <TableCell>
                    <div className="font-medium">{formatAffiliateCurrency(item.total_earnings || 0)}</div>
                    <div className="text-xs text-slate-500">Disponibles: {formatAffiliateCurrency(item.available_amount || 0)}</div>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${getAffiliateStatusToneClasses(item.status_code)}`}>
                      {getAffiliateStatusLabel(item.status_code)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Switch checked={Boolean(item.is_active)} onCheckedChange={() => handleToggleStatus(item.id, item.is_active)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredItems.length === 0 && (
            <div className="py-8 text-center text-sm text-slate-500">Aucun ambassadeur pour les filtres actuels.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActiveAmbassadorsManagement;
